import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ICarrierReference, ICarrierState, ICarrierReferencesState } from 'types/carrier'
import { closeReferencesDialog, editCarrierReferences } from 'utils/actions/carrier'
import uuid from 'uuid'
import { IStore } from 'apps/carrier/utils/reducers'
import { FormModal } from 'transfix-ui'
import { validatePhone } from 'utils/validators'
import AddReferenceEntry from './AddReferenceEntry'
import ReferenceEntry from './ReferenceEntry'

interface ISelector {
  carrier: ICarrierState
  carrierReferences: ICarrierReferencesState
}

const ReferencesDialog = () => {
  const dispatch = useDispatch()
  const closeDialog = () => {
    dispatch(closeReferencesDialog())
  }
  const {
    carrier: { carrierDetails },
    carrierReferences: { error, loading, references, showDialog },
  } = useSelector<IStore, ISelector>(({ carrier, carrierReferences }) => ({
    carrier,
    carrierReferences,
  }))
  const [localCarrierReferences, setLocalCarrierReferences] = useState<ICarrierReference[]>([])
  const [newReference, setNewReference] = useState<ICarrierReference>({
    companyName: '',
    phone: '',
    uuid: uuid(),
  })
  useEffect(() => {
    if (showDialog) {
      const newRefs = references.map((ref: ICarrierReference) => ({ ...ref, uuid: uuid() }))
      setLocalCarrierReferences(newRefs)
    }
  }, [showDialog, references])

  const updateReference = (e: { value: string; id: string }) => {
    const [referenceUuid, referenceType = 'reviewedById' as keyof ICarrierReference] = e.id.split(
      ':'
    )
    const newCarrierReferences = localCarrierReferences.map((reference: ICarrierReference) => {
      if (reference.uuid === referenceUuid) {
        if (referenceType === 'reviewedById') {
          reference.reviewedById = reference.reviewedById ? undefined : true
        } else {
          reference = { ...reference, [referenceType]: e.value }
        }
      }
      return reference
    })
    setLocalCarrierReferences(newCarrierReferences)
  }

  const updateNewReference = (e: { value: string; id: string }) => {
    setNewReference({
      ...newReference,
      [e.id]: e.value,
    })
  }

  const validateReference = (reference: ICarrierReference) => {
    if (validatePhone(reference.phone) && reference.companyName) {
      return { reference, containsErrors: false }
    }

    let errors = {}
    if (!validatePhone(reference.phone)) {
      errors = { ...errors, phoneValidation: 'Invalid Phone Number' }
    }
    if (!reference.companyName) {
      errors = { ...errors, companyValidation: 'Company Name must not be blank' }
    }

    return { reference: { ...reference, ...errors }, containsErrors: true }
  }

  const addNewReference = () => {
    const { containsErrors, reference } = validateReference(newReference)
    if (!containsErrors) {
      setLocalCarrierReferences([
        ...localCarrierReferences,
        {
          ...reference,
          uuid: uuid(),
          reviewedById: true,
          phoneValidation: '',
          companyValidation: '',
        },
      ])
      setNewReference({ companyName: '', phone: '', uuid: uuid() })
    } else {
      setNewReference(reference)
    }
  }

  const deleteReference = (uuidMatch: string) => {
    const newCarrierReferences = localCarrierReferences.map((reference: ICarrierReference) => {
      if (reference.uuid === uuidMatch) {
        return { ...reference, _destroy: true }
      }
      return reference
    })
    setLocalCarrierReferences(newCarrierReferences)
  }

  const saveReferences = () => {
    const sanitizeReferences = localCarrierReferences.filter(ref => ref.companyName && ref.phone)
    let referencesHaveErrors = false
    const checkReferences = localCarrierReferences.map(ref => {
      const { containsErrors, reference } = validateReference(ref)
      referencesHaveErrors = referencesHaveErrors || containsErrors
      return reference
    })
    if (referencesHaveErrors) {
      setLocalCarrierReferences(checkReferences)
    } else if (carrierDetails?.id) {
      dispatch(editCarrierReferences(carrierDetails?.id, sanitizeReferences))
    }
  }

  return (
    <FormModal
      title='Edit References'
      size={6}
      open={showDialog}
      onClose={closeDialog}
      error={error}
      dataTest='modal-edit-references'
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          disabled: loading,
          dataTest: 'cancel-edit-references',
        },
        {
          label: 'Save',
          type: 'confirm',
          disabled: loading,
          action: saveReferences,
          dataTest: 'save-edit-references',
        },
      ]}>
      <>
        <AddReferenceEntry
          reference={newReference}
          updateNewReference={updateNewReference}
          addNewReference={addNewReference}
        />
        {localCarrierReferences
          .filter(r => !r._destroy)
          .map((reference: ICarrierReference, idx: number) => (
            <ReferenceEntry
              key={reference.uuid}
              reference={reference}
              deleteReference={deleteReference}
              updateReference={updateReference}
              index={idx}
            />
          ))}
      </>
    </FormModal>
  )
}

export default ReferencesDialog
